/**
 * HTML Sanitization Utility for Super Admin App
 * 
 * Provides basic XSS protection for rich text content displayed in previews.
 * Removes dangerous tags while preserving safe formatting.
 */

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting
 * 
 * @param html - Raw HTML string from Firestore
 * @returns Sanitized HTML safe for rendering
 * 
 * @example
 * const clean = sanitizeHTML('<h2>Safe Title</h2><script>alert("XSS")</script>');
 * // Returns: '<h2>Safe Title</h2>' (script removed)
 */
export const sanitizeHTML = (html: string | null | undefined): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Create a temporary div element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Remove dangerous elements
  const dangerousTags = ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link', 'meta', 'style'];
  dangerousTags.forEach((tag) => {
    const elements = temp.querySelectorAll(tag);
    elements.forEach((el) => el.remove());
  });

  // Remove dangerous attributes from all elements
  const dangerousAttributes = ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit'];
  const allElements = temp.querySelectorAll('*');
  allElements.forEach((el) => {
    dangerousAttributes.forEach((attr) => {
      el.removeAttribute(attr);
    });
  });

  // Remove javascript: from href attributes
  const links = temp.querySelectorAll('a[href]');
  links.forEach((link) => {
    const href = link.getAttribute('href');
    if (href && href.toLowerCase().startsWith('javascript:')) {
      link.removeAttribute('href');
    }
  });

  return temp.innerHTML;
};
